import { computed, effect } from '@angular/core';
import { UserAccountDto, UserDto } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SideSwitcherMode } from '@shared/enums/side-switcher-mode';
import { isStrategyAccount, resolveActiveAccount } from './current-user.utils';
import { environment } from 'environments/environment';
import { Observable, pipe, switchMap, tap } from 'rxjs';

const ACTIVE_ACCOUNT_STORAGE_KEY = 'activeAccountId';

function toAccountId(value: string | null): number | undefined {
  const accountId = Number(value);
  return Number.isInteger(accountId) && accountId > 0 ? accountId : undefined;
}

// sessionStorage belongs to the tab and survives its reload (also when the browser discards a
// background tab), so a tab keeps its own account. localStorage is shared by all tabs and only
// picks the account a new tab starts on.
function readStoredAccountId(): number | undefined {
  try {
    return (
      toAccountId(sessionStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY)) ??
      toAccountId(localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY))
    );
  } catch {
    return undefined;
  }
}

function storeTabAccountId(accountId: number) {
  try {
    sessionStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, String(accountId));
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for this session only.
  }
}

function storeAccountId(accountId: number) {
  storeTabAccountId(accountId);
  try {
    localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, String(accountId));
  } catch {
    // See storeTabAccountId.
  }
}

export interface CurrentUserService {
  getCurrentUser(): Observable<UserDto>;
}

export const useCurrentUser = () =>
  signalStoreFeature(
    {
      props: {
        _usersService: type<CurrentUserService>(),
      },
    },
    withState({
      currentUser: undefined as UserDto | undefined,
      currentUserLoaded: false,
      selectedAccountId: readStoredAccountId(),
    }),
    withComputed(store => {
      const is2FAEnabled = computed(() => !!store.currentUser()?.enabled2FA);

      const canTrade = computed(
        () => store.currentUser()?.subRole !== UserDto.SubRoleEnum.ReadOnly,
      );

      const sideSwitcherMode = computed(() =>
        store.currentUser()?.subRole === UserDto.SubRoleEnum.TraderPro
          ? SideSwitcherMode.PRO
          : SideSwitcherMode.BASIC,
      );

      const isSsoOnly = computed(() => !!store.currentUser()?.ssoOnly);

      // A strategy subaccount has exactly one view, the embedded CoinLion UI, and must never be
      // shown the OTC one. Where that UI is switched off the account therefore has no view at
      // all, so it is dropped here rather than offered: this is the one gate every consumer of
      // the account list -- the selector, the active-account resolution, transfers -- goes
      // through.
      const accounts = computed<UserAccountDto[]>(() => {
        const visible = store.currentUser()?.accounts ?? [];

        return environment.coinlionEnabled
          ? visible
          : visible.filter(account => !isStrategyAccount(account));
      });

      const activeAccount = computed(() =>
        resolveActiveAccount(accounts(), store.selectedAccountId()),
      );

      const activeAccountId = computed(() => activeAccount()?.id);

      const isSubAccountActive = computed(
        () => !!activeAccount() && !activeAccount()?.main,
      );

      // The embedded strategy UI is issued per account and only a strategy subaccount has one.
      const isStrategyAccountActive = computed(() => isStrategyAccount(activeAccount()));

      const mainAccount = computed(() => accounts().find(account => account.main));

      // Funds move between the main account and a subaccount, so the user must see both.
      const canMoveFunds = computed(
        () => canTrade() && !!mainAccount() && accounts().some(account => !account.main),
      );

      return {
        is2FAEnabled,
        canTrade,
        sideSwitcherMode,
        isSsoOnly,
        accounts,
        activeAccount,
        activeAccountId,
        isSubAccountActive,
        isStrategyAccountActive,
        mainAccount,
        canMoveFunds,
      };
    }),
    withMethods(store => {
      const loadCurrentUser = rxMethod<void>(
        pipe(
          // Guards wait on this flag, so an earlier load must not answer for this one.
          tap(() => patchState(store, { currentUserLoaded: false })),
          switchMap(() =>
            store._usersService.getCurrentUser().pipe(
              tapResponse({
                next: (currentUser: UserDto) =>
                  patchState(store, { currentUser, currentUserLoaded: true }),
                // An unauthenticated user hitting this is expected and handled by the auth guard.
                error: () => patchState(store, { currentUserLoaded: true }),
              }),
            ),
          ),
        ),
      );

      const setCurrentUser = (currentUser: UserDto) => patchState(store, { currentUser });

      const selectAccount = (accountId: number) => {
        storeAccountId(accountId);
        patchState(store, { selectedAccountId: accountId });
      };

      return { loadCurrentUser, setCurrentUser, selectAccount };
    }),
    withHooks({
      onInit(store) {
        // A tab that never switched still has to keep the account it shows when another tab switches.
        effect(() => {
          const accountId = store.activeAccountId();

          if (accountId) {
            storeTabAccountId(accountId);
          }
        });
      },
    }),
  );
